// Crochets du panneau Navigateur : session courante de la conversation, flux d'images et envoi
// des gestes du membre quand il a pris la main. Portes de use-browser-screencast.ts et
// use-browser-input.ts de l'ancienne app Pirouette.
import type { MessageFlux } from "@app/lib/compta/navigateur/flux";
import { abonnerFluxNavigateur } from "@app/lib/compta/navigateur/flux";
import { registreNavigateur } from "@app/lib/compta/navigateur/ouverture";
import type { EvenementSaisie } from "@app/lib/compta/navigateur/saisie";
import {
  HAUTEUR_ECRAN_DEFAUT,
  LARGEUR_ECRAN_DEFAUT,
  TAILLE_LOT,
} from "@app/lib/compta/navigateur/saisie";
import type { SessionNavigateur } from "@app/lib/compta/navigateur/session";
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";

export function useSessionNavigateur(
  conversationId: string | null
): SessionNavigateur | null {
  return useSyncExternalStore(
    registreNavigateur.abonner,
    () => (conversationId ? registreNavigateur.session(conversationId) : null),
    () => null
  );
}

/**
 * `bloque` : le flux est ouvert mais aucune image n'arrive (vu en production dans l'ancienne
 * app : sans cet etat, rien ne distingue « ca arrive » de « ca n'arrivera jamais »).
 * `terminee` : jeton expire (etat en 401) ou session fermee.
 */
export type StatutNavigateur =
  | "sansSession"
  | "connexion"
  | "actif"
  | "repos"
  | "indisponible"
  | "nonPrisEnCharge"
  | "bloque"
  | "terminee";

export interface ImageNavigateur {
  donnees: string;
  largeur: number;
  hauteur: number;
}

const DELAI_PREMIERE_IMAGE_MS = 8_000;

export function useFluxNavigateur(session: SessionNavigateur | null): {
  image: ImageNavigateur | null;
  statut: StatutNavigateur;
  url: string | null;
  titre: string | null;
} {
  const [image, setImage] = useState<ImageNavigateur | null>(null);
  const [statut, setStatut] = useState<StatutNavigateur>(
    session ? "connexion" : "sansSession"
  );
  const [url, setUrl] = useState<string | null>(session?.url ?? null);
  const [titre, setTitre] = useState<string | null>(session?.titre ?? null);
  const streamUrl = session?.streamUrl ?? null;
  const etatUrl = session?.etatUrl ?? null;
  const urlDuResultat = session?.url ?? null;
  const titreDuResultat = session?.titre ?? null;

  // Le resultat d'outil dit deja quelle page est ouverte : la barre l'affiche avant la 1re image.
  useEffect(() => {
    if (urlDuResultat) {
      setUrl(urlDuResultat);
      setTitre(titreDuResultat);
    }
  }, [urlDuResultat, titreDuResultat]);

  useEffect(() => {
    if (!streamUrl || !etatUrl) {
      setStatut("sansSession");
      return;
    }
    // Un jeton neuf (nouveau resultat d'outil) relance le flux, sans effacer la derniere image :
    // c'est le meme navigateur.
    setStatut((s) => (s === "actif" ? s : "connexion"));
    let imageVue = false;
    let minuteur: ReturnType<typeof setTimeout> | undefined;
    const armer = () => {
      if (minuteur !== undefined) {
        return;
      }
      minuteur = setTimeout(() => {
        if (!imageVue) {
          setStatut("bloque");
        }
      }, DELAI_PREMIERE_IMAGE_MS);
    };
    const desarmer = () => {
      if (minuteur !== undefined) {
        clearTimeout(minuteur);
        minuteur = undefined;
      }
    };

    const surMessage = (m: MessageFlux) => {
      if (m.kind === "frame") {
        imageVue = true;
        desarmer();
        setImage({
          donnees: m.data,
          largeur: m.metadata.deviceWidth ?? LARGEUR_ECRAN_DEFAUT,
          hauteur: m.metadata.deviceHeight ?? HAUTEUR_ECRAN_DEFAUT,
        });
        setStatut("actif");
        return;
      }
      if (m.url !== undefined) {
        setUrl(m.url && m.url !== "about:blank" ? m.url : null);
      }
      if (m.title !== undefined) {
        setTitre(m.title || null);
      }
      if (m.state === "active") {
        if (imageVue) {
          setStatut("actif");
        } else {
          setStatut("connexion");
          armer();
        }
      } else if (m.state === "idle") {
        desarmer();
        setStatut("repos");
      } else {
        desarmer();
        setStatut("indisponible");
      }
    };

    const arreter = abonnerFluxNavigateur({
      streamUrl,
      etatUrl,
      surMessage,
      surTerminee: () => {
        desarmer();
        setStatut("terminee");
      },
      surNonPrisEnCharge: () => {
        desarmer();
        setStatut("nonPrisEnCharge");
      },
      surReconnexion: () => {
        setStatut((s) => (s === "actif" || s === "repos" ? "indisponible" : s));
      },
    });
    armer();
    return () => {
      desarmer();
      arreter();
    };
  }, [streamUrl, etatUrl]);

  return { image, statut, url, titre };
}

export interface ControleSaisie {
  envoyer: (e: EvenementSaisie) => void;
  aInteragi: () => boolean;
  reinitialiser: () => void;
}

/**
 * File d'attente des gestes : un lot a la fois (64 au plus), l'ordre est garde. Un echec vide la
 * file (on ne rejoue pas de vieux clics) et rend la main a l'assistant.
 */
export function useSaisieNavigateur(
  inputUrl: string | null,
  surEchec: () => void
): ControleSaisie {
  const file = useRef<EvenementSaisie[]>([]);
  const enVol = useRef(false);
  const envoye = useRef(false);
  // Derniere adresse et dernier rappel lus au moment de l'envoi, pas a la creation du crochet :
  // un jeton neuf arrive pendant qu'un lot est en vol ne doit pas renvoyer vers l'ancien.
  const adresse = useRef(inputUrl);
  const rappel = useRef(surEchec);
  adresse.current = inputUrl;
  rappel.current = surEchec;

  const vider = useCallback(async () => {
    const cible = adresse.current;
    if (enVol.current || !cible || file.current.length === 0) {
      return;
    }
    enVol.current = true;
    const lot = file.current.splice(0, TAILLE_LOT);
    try {
      const res = await fetch(cible, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ events: lot }),
      });
      if (!res.ok) {
        throw new Error(`saisie navigateur ${res.status}`);
      }
      envoye.current = true;
    } catch {
      file.current.length = 0;
      rappel.current();
    } finally {
      enVol.current = false;
      if (file.current.length > 0) {
        void vider();
      }
    }
  }, []);

  const envoyer = useCallback(
    (e: EvenementSaisie) => {
      file.current.push(e);
      void vider();
    },
    [vider]
  );

  return {
    envoyer,
    aInteragi: useCallback(() => envoye.current, []),
    reinitialiser: useCallback(() => {
      envoye.current = false;
      file.current.length = 0;
    }, []),
  };
}
